// The SINGLE builder for the canonical answer state (#4008 P0).
//
// It lives beside the contract, in packages/lib, for one reason: the defect
// class this whole lane exists to kill is "two places deciding the same thing".
// A builder on the server and a second builder in the browser would recreate it
// on day one. The API produces the state with this function and publishes it;
// the browser BINDS to the published object and calls nothing.
//
// PROJECTION, NOT CALCULATOR (sol's PR review on #4093, blocking item 1). The
// first cut accepted independent amount slots and then recomputed the committed
// residual, the plan-of-record residual, and a "surplus" over a basis it had
// just declared non-additive. That is the recomputation drift this contract
// exists to remove. This version:
//
//   * takes the solver's `ObjectivePackageSolution` as the packaging authority
//     and reads residual / targetFill / receipt ids straight off its coverage;
//   * performs NO arithmetic of its own — not one subtraction;
//   * refuses to publish a figure the solver did not produce, holding instead.
//
// FAIL CLOSED (sol's PR review, blocking item 2). Nothing is admitted unless a
// condition affirmatively passes. A missing governance panel, an empty facet
// roster, an unmatched category key, a `modeled` facet, or a stale as-of all
// leave the primary UNSELECTED with a named unmet condition. The first cut
// recommended unless something proved it bad.
//
// HONEST HOLDS (sol's PR review, blocking item 4). Every hold carries a `kind`.
// `threading_pending` means the fact exists on this answer and has not been
// wired here yet — engineering work, and the reader is told so rather than sent
// to reconnect a source that is already connected.

import type {
  ObjectivePackageAuthority,
  ObjectivePackageAuthorityWithheldReason,
  ObjectivePackageOverlapReceiptState,
  ObjectivePackageSolution,
} from './objective-package-solver';
import {
  EXECUTIVE_ANSWER_OVERLAP_RECEIPT_STANDARD,
  EXECUTIVE_ANSWER_STATE_SCHEMA_VERSION,
  EXECUTIVE_GOVERNANCE_FACET_AXIS_IDS,
  EXECUTIVE_HEADER_COUNTING_AXIS_ID,
  EXECUTIVE_PRIMARY_SELECTION_POLICY_ID,
  type ExecutiveAnswerCounts,
  type ExecutiveAnswerHold,
  type ExecutiveAnswerLineage,
  type ExecutiveAnswerMetric,
  type ExecutiveAnswerPostureMix,
  type ExecutiveAnswerProvenance,
  type ExecutiveAnswerResidual,
  type ExecutiveAnswerResidualWithholding,
  type ExecutiveAnswerState,
  type ExecutiveAnswerVerdict,
  type ExecutiveGovernanceAxisCount,
  type ExecutiveGovernanceFacetAxisId,
  type ExecutivePrimaryDecisionPackage,
  type ExecutivePrimaryRejection,
  type ExecutivePrimarySelection,
  type ExecutiveReferenceLens,
  type ExecutiveScenarioSelection,
  type ExecutiveWorkforceState,
} from './executive-answer-state';

const OWNER_FINANCE = 'Finance';
const OWNER_ENGINEERING = 'Helix engineering';

/** A figure the producer resolved: its value, and the note when it renders dimmed. */
export type ExecutiveAnswerSlotInput = {
  value: number | null;
  review: string | null;
  /**
   * True when the producer KNOWS this fact exists on the answer packet but has
   * not threaded it into this projection. Drives `threading_pending` holds so a
   * plumbing gap never renders as a business gap.
   */
  threadingPending?: boolean;
};

/**
 * The case-ledger's own reason for publishing no receipt-backed disjoint
 * package. A closed code — the hold copy is derived from it, not re-authored per
 * surface.
 */
export type ExecutiveAnswerFloorReasonCode =
  | 'no_governed_lever_contribution'
  | 'no_receipted_disjoint_package';

/**
 * Governance arming for the candidate, read from the value-governance facets.
 *
 * FAIL CLOSED. `expectedFacetIds` is the roster the governance producer says
 * MUST be live. An empty roster is not "nothing blocks it" — it is "the roster
 * did not resolve", and that fails.
 */
export type ExecutiveAnswerGovernanceInput = {
  /** True only when a governance panel resolved at all. */
  panelResolved: boolean;
  /** The facets the producer requires to be live. Empty => cannot arm. */
  expectedFacetIds: readonly string[];
  /** Facets whose status is exactly `live`. */
  liveFacetIds: readonly string[];
  /** Facets whose status is `modeled`. Modeled never arms a recommendation. */
  modeledFacetIds: readonly string[];
  /** Facets whose status is `not_yet_armed`. */
  notYetArmedFacetIds: readonly string[];
  /** Lever families whose governance blocks the candidate. */
  blockingLeverFamilyIds: readonly string[];
  /**
   * Per-facet as-of, ISO, keyed by the SAME scoped facet id used in
   * `expectedFacetIds`. Sol executed the first cut's fail-open: the adapter
   * published only the NEWEST timestamp across all facets, so a source binding
   * dated 2024-01-01 masked by a fresh owner binding dated 2026-07-29 armed the
   * candidate. Freshness is per-facet now, and the OLDEST required facet
   * governs — one stale required check blocks arming.
   *
   * A required facet missing from this map is UNDATED, which also blocks.
   */
  facetAsOfById: Readonly<Record<string, string | null>>;
  /** The run's as-of, ISO. A facet older than the freshness window fails. */
  runAsOf: string | null;
  /** Maximum permitted age, in days, between facet as-of and run as-of. */
  freshnessWindowDays: number;
};

/** A named member of a package or lens. */
export type ExecutiveAnswerMemberInput = {
  memberId: string;
  label: string;
  amountUsd: number | null;
  posture: 'receipted' | 'modeled';
};

export type ExecutiveAnswerCommittedFloorInput = {
  /** The receipt-backed subtotal. The ONLY figure this policy may commit. */
  receiptedAmountUsd: number | null;
  /** The published floor including any modeled admission. */
  publishedAmountUsd: number | null;
  members: readonly ExecutiveAnswerMemberInput[];
  receiptIds: readonly string[];
  overlapReceiptState: ObjectivePackageOverlapReceiptState;
  missingReceiptPairs: readonly string[];
  /** The ledger's stated reason when it published no floor. */
  reasonCode: ExecutiveAnswerFloorReasonCode | null;
  /** The ledger's own detail sentence, copied verbatim when present. */
  reasonDetail: string | null;
  asOf: string | null;
  review: string | null;
  threadingPending?: boolean;
};

export type ExecutiveAnswerCandidateInput = ExecutiveAnswerSlotInput & {
  memberIds: readonly string[];
  /** Non-null when the candidate's own payload lineage disagrees with itself. */
  lineage: ExecutiveAnswerLineage;
  provenance: ExecutiveAnswerProvenance | null;
};

export type ExecutiveAnswerLensAmountInput = ExecutiveAnswerSlotInput & {
  memberIds: readonly string[];
  provenance: ExecutiveAnswerProvenance | null;
  /**
   * The gap remaining against this lens's OWN basis, as already published by its
   * producer. Carried, never computed — and only meaningful on a lens whose
   * members can be added together at all. A `not_additive` lens must leave it
   * null; the invariant check enforces that.
   */
  publishedResidualUsd?: number | null;
};

export type ExecutiveAnswerGovernanceFacetCountsInput = Readonly<
  Partial<
    Record<
      ExecutiveGovernanceFacetAxisId,
      { live: number; modeled: number; notYetArmed: number }
    >
  >
>;

export type ExecutiveAnswerStateInput = {
  metric: {
    /** The literal metric the reader asked about. */
    askedMetricLabel: string | null;
    /** The metric this answer is framed on. */
    answeredMetricLabel: string | null;
    /**
     * The governed metric-preference mapping sentence, supplied by the producer
     * when — and only when — a mapping actually resolved the asked metric onto a
     * different answered metric. Never authored here.
     */
    suppliedMappingNote: string | null;
    threadingPending?: boolean;
  };
  objective: {
    baseline: ExecutiveAnswerSlotInput;
    target: ExecutiveAnswerSlotInput;
    requiredUplift: ExecutiveAnswerSlotInput;
  };
  committedFloor: ExecutiveAnswerCommittedFloorInput;
  /** The selected target-gap package. A CANDIDATE, never a primary. */
  candidatePackage: ExecutiveAnswerCandidateInput;
  candidateGovernance: ExecutiveAnswerGovernanceInput;
  /** The company's approved plan of record, when a governed figure carries it. */
  planOfRecord: ExecutiveAnswerLensAmountInput;
  /** Net after the maximum deduction across DECLARED overlap pairs. */
  planningLens: ExecutiveAnswerLensAmountInput & {
    includesModeledInputs: boolean;
    includesGatedInputs: boolean;
  };
  /**
   * The standalone lever inventory. Always NOT ADDITIVE: its members are not
   * proven separate, so it never carries target coverage, a residual, or a fill.
   */
  standaloneInventory: ExecutiveAnswerLensAmountInput;
  evidencePostureCounts: ExecutiveAnswerCounts;
  governanceFacetCounts: ExecutiveAnswerGovernanceFacetCountsInput;
  workforce: {
    inclusion: ExecutiveWorkforceState['inclusion'];
    amountUsd: number | null;
    countedInLensIds: readonly string[];
    threadingPending?: boolean;
  };
  scenario: {
    selectedCaseId: string | null;
    criteriaApplied: readonly string[];
    scenarioMetricId: string | null;
    answeredMetricId: string | null;
  };
  /** The solver's solution, CONSUMED as the packaging spine. */
  packageSpine: ObjectivePackageSolution | null;
  /** Compact USD formatter supplied by the caller; no formatting lives here. */
  formatUsd: (value: number) => string;
};

// ---------------------------------------------------------------------------
// Holds
// ---------------------------------------------------------------------------

function threadingHold(what: string): ExecutiveAnswerHold {
  return {
    kind: 'threading_pending',
    reasonPlain:
      `${what} is present on this answer but is not yet carried into this reconciled ` +
      'view, so it is not shown here.',
    unlockAction:
      'No action is needed from you — connecting it here is Helix engineering work, ' +
      'already on the list.',
    unlockOwner: OWNER_ENGINEERING,
  };
}

function evidenceHold(
  reasonPlain: string,
  unlockAction: string,
  unlockOwner: string | null = OWNER_FINANCE
): ExecutiveAnswerHold {
  return { kind: 'evidence_missing', reasonPlain, unlockAction, unlockOwner };
}

function policyHold(
  reasonPlain: string,
  unlockAction: string,
  unlockOwner: string | null = OWNER_FINANCE
): ExecutiveAnswerHold {
  return { kind: 'policy_blocked', reasonPlain, unlockAction, unlockOwner };
}

function slotHold(
  slot: { review: string | null; threadingPending?: boolean },
  what: string,
  unlockAction: string
): ExecutiveAnswerHold {
  if (slot.threadingPending) return threadingHold(what);
  if (slot.review !== null) {
    return evidenceHold(slot.review, unlockAction);
  }
  return evidenceHold(`${what} has not been published on this answer.`, unlockAction);
}

function objectiveHold(
  what: string,
  slot: ExecutiveAnswerSlotInput
): ExecutiveAnswerHold {
  if (slot.threadingPending) return threadingHold(`The approved figure for ${what}`);
  if (slot.review !== null) {
    return evidenceHold(slot.review, 'Resolve the condition named above, then re-run this answer.');
  }
  return evidenceHold(
    `No approved figure for ${what} is connected to this answer.`,
    `Connect an approved figure for ${what}.`
  );
}

// ---------------------------------------------------------------------------
// Governance eligibility — FAIL CLOSED
// ---------------------------------------------------------------------------

type EligibilityOutcome = {
  armed: boolean;
  conditionsMet: string[];
  conditionsUnmet: string[];
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Every condition must pass affirmatively. Sol's PR review named five ways the
 * first gate failed open; each is now its own named condition, and each failure
 * appears verbatim in the selection receipt.
 */
export function evaluateCandidateEligibility(
  input: ExecutiveAnswerGovernanceInput
): EligibilityOutcome {
  const met: string[] = [];
  const unmet: string[] = [];

  if (input.panelResolved) met.push('a governance panel resolved for this answer');
  else unmet.push('no governance panel resolved for this answer');

  if (input.expectedFacetIds.length > 0) {
    met.push('the governance producer named the checks this figure requires');
  } else {
    unmet.push('the governance producer named no required checks, so none can be confirmed');
  }

  const live = new Set(input.liveFacetIds);
  const missingLive = input.expectedFacetIds.filter((id) => !live.has(id));
  if (input.expectedFacetIds.length > 0 && missingLive.length === 0) {
    met.push('every required check is complete against an approved record');
  } else if (missingLive.length > 0) {
    unmet.push(`these required checks are not complete: ${missingLive.join(', ')}`);
  }

  if (input.modeledFacetIds.length === 0) {
    met.push('no required check rests on a modeled value');
  } else {
    unmet.push(
      `these checks rest on modeled values rather than approved records: ${input.modeledFacetIds.join(
        ', '
      )}`
    );
  }

  if (input.notYetArmedFacetIds.length === 0) {
    met.push('no required check is still outstanding');
  } else {
    unmet.push(`these checks are still outstanding: ${input.notYetArmedFacetIds.join(', ')}`);
  }

  if (input.blockingLeverFamilyIds.length === 0) {
    met.push('no move behind this figure is blocked');
  } else {
    unmet.push(`these moves are blocked: ${input.blockingLeverFamilyIds.join(', ')}`);
  }

  // Freshness, PER REQUIRED FACET. A stale check is not a passed check, and a
  // fresh sibling does not make it one.
  const runAsOf = input.runAsOf ? Date.parse(input.runAsOf) : NaN;
  if (!Number.isFinite(runAsOf)) {
    unmet.push('this answer carries no date, so the currency of its checks cannot be confirmed');
  } else if (input.expectedFacetIds.length > 0) {
    const stale: string[] = [];
    const undated: string[] = [];
    let oldestAgeDays = 0;
    for (const facetId of input.expectedFacetIds) {
      const raw = input.facetAsOfById[facetId] ?? null;
      const parsed = raw ? Date.parse(raw) : NaN;
      if (!Number.isFinite(parsed)) {
        undated.push(facetId);
        continue;
      }
      const ageDays = Math.abs(runAsOf - parsed) / DAY_MS;
      if (ageDays > oldestAgeDays) oldestAgeDays = ageDays;
      if (ageDays > input.freshnessWindowDays) stale.push(facetId);
    }
    if (undated.length > 0) {
      unmet.push(
        `these checks carry no date, so their currency cannot be confirmed: ${undated.join(', ')}`
      );
    }
    if (stale.length > 0) {
      unmet.push(
        `these checks are older than the ${input.freshnessWindowDays}-day window: ` +
          `${stale.join(', ')}`
      );
    }
    if (undated.length === 0 && stale.length === 0) {
      met.push(
        `every check behind this figure is current (oldest is ${Math.round(oldestAgeDays)} days old)`
      );
    }
  }

  return { armed: unmet.length === 0, conditionsMet: met, conditionsUnmet: unmet };
}

// ---------------------------------------------------------------------------
// Package authority projection — no recomputation
// ---------------------------------------------------------------------------

function authorityResidual(
  authority: ObjectivePackageAuthority | null
): ExecutiveAnswerResidual | null {
  if (authority?.residualAgainstSelectedManagementPackage.kind !== 'measured') return null;
  return {
    basis: 'measured',
    amountUsd: authority.residualAgainstSelectedManagementPackage.residualGapValue,
  };
}

function authorityTargetFillPct(authority: ObjectivePackageAuthority | null): number | null {
  return authority?.residualAgainstSelectedManagementPackage.kind === 'measured'
    ? authority.residualAgainstSelectedManagementPackage.targetFillPct
    : null;
}

function emptyPostureMix(): ExecutiveAnswerPostureMix {
  return {
    receiptedMemberCount: 0,
    receiptedAmountUsd: null,
    modeledMemberCount: 0,
    modeledAmountUsd: null,
    mixDisclosurePlain: null,
  };
}

function authorityPostureMix(
  authority: ObjectivePackageAuthority | null
): ExecutiveAnswerPostureMix {
  if (authority === null) return emptyPostureMix();
  return {
    receiptedMemberCount: authority.dependableMinimum.memberIds.length,
    receiptedAmountUsd: authority.dependableMinimum.amountValue,
    modeledMemberCount: authority.selectedEstimatedAddition.memberIds.length,
    modeledAmountUsd: authority.selectedEstimatedAddition.amountValue,
    mixDisclosurePlain: authority.selectedEstimatedAddition.disclosure,
  };
}

function packageAuthorityHold(
  reason: ObjectivePackageAuthorityWithheldReason | null
): ExecutiveAnswerHold {
  if (reason === null) {
    return evidenceHold(
      'No selected management package is available for this answer.',
      'Refresh this answer once the selected management package is available.'
    );
  }
  if (reason.reasonCode === 'selected_member_not_additive') {
    return policyHold(
      reason.reasonPlain,
      'Reconcile the selected moves until they are shown to release separate dollars.'
    );
  }
  if (reason.reasonCode === 'selected_modeled_disclosure_missing') {
    return policyHold(
      reason.reasonPlain,
      'Add the modeled-value disclosure for the selected move, then re-run this answer.'
    );
  }
  if (reason.reasonCode === 'anchor_blocked') {
    return evidenceHold(
      reason.reasonPlain,
      'Connect the target anchor so the selected package can be measured against it.'
    );
  }
  if (reason.reasonCode === 'selected_member_source_unknown') {
    return evidenceHold(
      reason.reasonPlain,
      'Connect source posture for the selected members, then re-run this answer.'
    );
  }
  if (reason.reasonCode === 'selected_member_receipt_missing') {
    return evidenceHold(
      reason.reasonPlain,
      'Connect exact contribution receipt ids for the selected members, then re-run this answer.'
    );
  }
  return evidenceHold(
    reason.reasonPlain,
    'Select at least one source-backed or disclosed modeled move for the management package.'
  );
}

const NOT_ADDITIVE_RESIDUAL_WITHHOLDING: ExecutiveAnswerResidualWithholding = {
  reasonCode: 'not_additive',
  reasonPlain:
    'These moves have not been shown to release separate dollars, so they cannot be added ' +
    'together and measured against the target as one package. No remaining gap is shown ' +
    'against them for that reason — not because one has not been worked out.',
};

const NO_MEASURED_BASIS_WITHHOLDING: ExecutiveAnswerResidualWithholding = {
  reasonCode: 'no_measured_basis',
  reasonPlain:
    'No producer has published a remaining gap against this figure, so none is shown. ' +
    'It appears as soon as one is published.',
};

// ---------------------------------------------------------------------------
// Metric
// ---------------------------------------------------------------------------

function buildMetric(input: ExecutiveAnswerStateInput['metric']): ExecutiveAnswerMetric {
  const asked = input.askedMetricLabel?.trim() || null;
  const answered = input.answeredMetricLabel?.trim() || null;
  const differ =
    asked !== null && answered !== null && asked.toLowerCase() !== answered.toLowerCase();
  return {
    askedMetricLabel: asked,
    answeredMetricLabel: answered,
    // The mapping sentence renders only when a mapping actually happened AND the
    // producer supplied its wording. Two metrics that match have no mapping to
    // state; a mapping with no supplied sentence is not invented here.
    mappingNote: differ ? input.suppliedMappingNote?.trim() || null : null,
    hold:
      asked === null && answered === null
        ? input.threadingPending
          ? threadingHold('The measure this answer is framed on')
          : evidenceHold(
              'No approved figure on this answer names the measure it is framed on.',
              'Connect an approved figure that names its measure, then re-run this answer.'
            )
        : null,
  };
}

// ---------------------------------------------------------------------------
// Primary decision package — POLICY selection, never amount size
// ---------------------------------------------------------------------------

const PRIMARY_RULE_APPLIED =
  'The selected management package combines the dependable minimum and the disclosed ' +
  'modeled addition. Its remaining gap and target coverage are measured against that ' +
  'package. Size is never a reason to select it.';

function buildPrimaryDecisionPackage(input: {
  floor: ExecutiveAnswerCommittedFloorInput;
  candidateRejections: readonly ExecutivePrimaryRejection[];
  packageSpine: ObjectivePackageSolution | null;
  packageAuthority: ObjectivePackageAuthority | null;
}): ExecutivePrimaryDecisionPackage {
  const { floor, packageAuthority, packageSpine } = input;
  const selectedPackage = packageAuthority?.selectedManagementPackage ?? null;
  const postureMix = authorityPostureMix(packageAuthority);
  const selectedAmountUsd = selectedPackage?.amountValue ?? null;
  const selectedMemberIds = selectedPackage?.memberIds ?? [];
  const selectedReceiptIds = selectedPackage?.receiptIds ?? [];
  const selectedBindings = selectedPackage?.memberReceiptBindings ?? [];

  const conditionsMet: string[] = [];
  const conditionsUnmet: string[] = [];
  const rejected: ExecutivePrimaryRejection[] = [...input.candidateRejections];

  if (packageSpine !== null) {
    conditionsMet.push('an authoritative package calculation is available for this answer');
  } else {
    conditionsUnmet.push('no authoritative package calculation is available for this answer');
  }
  if (packageAuthority !== null) {
    conditionsMet.push('the authoritative package definition is available');
  } else {
    conditionsUnmet.push('the authoritative package definition is unavailable');
  }
  if (selectedAmountUsd !== null) {
    conditionsMet.push('the selected management package amount is available');
  } else {
    conditionsUnmet.push(
      selectedPackage?.withheldReason?.reasonPlain ??
        'the selected management package amount is unavailable'
    );
  }
  if (selectedMemberIds.length > 0) {
    conditionsMet.push('the selected management package names its exact members');
  } else {
    conditionsUnmet.push('the selected management package names no members');
  }
  if (selectedReceiptIds.length > 0) {
    conditionsMet.push('the selected management package carries approved source references');
  } else {
    conditionsUnmet.push('the selected management package carries no approved source references');
  }
  if (memberBindingsMatchSelectedMembers(selectedBindings, selectedMemberIds)) {
    conditionsMet.push('each selected package member has its own approved source reference');
  } else {
    conditionsUnmet.push('selected package members are not matched to their approved source references');
  }
  if (packageAuthority?.residualAgainstSelectedManagementPackage.kind === 'measured') {
    conditionsMet.push('the remaining gap and target coverage are measured against that package');
  } else if (packageAuthority?.residualAgainstSelectedManagementPackage.kind === 'withheld') {
    conditionsUnmet.push(
      packageAuthority.residualAgainstSelectedManagementPackage.reason.reasonPlain
    );
  } else {
    conditionsUnmet.push('the remaining gap and target coverage are unavailable');
  }

  const selection: ExecutivePrimarySelection = {
    policyId: EXECUTIVE_PRIMARY_SELECTION_POLICY_ID,
    ruleApplied: PRIMARY_RULE_APPLIED,
    conditionsMet,
    conditionsUnmet,
    rejected,
  };

  if (conditionsUnmet.length > 0) {
    return {
      packageId: null,
      label: 'The amount you could commit to',
      amountUsd: null,
      posture: 'absent',
      memberIds: [],
      receiptIds: selectedReceiptIds,
      postureMix,
      provenance: null,
      overlapReceiptState: selectedReceiptIds.length > 0 ? 'receipted' : null,
      missingReceiptPairs: [],
      residual: null,
      targetFillPct: null,
      selection,
      hold: packageAuthorityHold(packageAuthorityHoldReason(packageAuthority)),
    };
  }

  return {
    packageId: 'selected_management_package',
    label: 'Selected management package',
    amountUsd: selectedAmountUsd,
    posture: postureMix.modeledMemberCount > 0 ? 'management_package' : 'committed',
    memberIds: selectedMemberIds,
    receiptIds: selectedReceiptIds,
    postureMix,
    provenance: {
      producerId: packageAuthority?.provenance.producerId ?? 'objective_package_solver_v1',
      sourceFactPath: 'packageSpine.packageAuthority.selectedManagementPackage',
      asOf: packageAuthority?.asOf ?? packageSpine?.generatedAt ?? floor.asOf,
      includesModeledInputs: postureMix.modeledMemberCount > 0,
      includesGatedInputs: false,
      receiptIds: selectedReceiptIds,
    },
    overlapReceiptState: 'receipted',
    missingReceiptPairs: [],
    residual: authorityResidual(packageAuthority),
    targetFillPct: authorityTargetFillPct(packageAuthority),
    selection,
    hold: null,
  };
}

function packageAuthorityHoldReason(
  authority: ObjectivePackageAuthority | null
): ObjectivePackageAuthorityWithheldReason | null {
  if (authority === null) return null;
  if (authority.selectedManagementPackage.withheldReason !== null) {
    return authority.selectedManagementPackage.withheldReason;
  }
  if (authority.residualAgainstSelectedManagementPackage.kind === 'withheld') {
    return authority.residualAgainstSelectedManagementPackage.reason;
  }
  return null;
}

function memberBindingsMatchSelectedMembers(
  bindings: readonly { memberId: string }[],
  memberIds: readonly string[]
): boolean {
  if (memberIds.length === 0 || bindings.length !== memberIds.length) return false;
  const bindingIds = bindings.map((binding) => binding.memberId).sort();
  const selectedIds = [...memberIds].sort();
  return bindingIds.every((id, index) => id === selectedIds[index]);
}

// ---------------------------------------------------------------------------
// Verdict
// ---------------------------------------------------------------------------

function buildVerdict(input: {
  primary: ExecutivePrimaryDecisionPackage;
  requiredUplift: number | null;
  planningLensAmount: number | null;
  formatUsd: (value: number) => string;
}): ExecutiveAnswerVerdict {
  const { primary, requiredUplift, planningLensAmount, formatUsd } = input;
  const upliftText = requiredUplift !== null ? formatUsd(requiredUplift) : null;

  if (primary.posture === 'committed' && primary.amountUsd !== null) {
    const amountText = formatUsd(primary.amountUsd);
    const residual = primary.residual?.basis === 'measured' ? primary.residual.amountUsd : null;
    if (residual !== null && residual <= 0) {
      return {
        line: 'The amount you can commit to covers the uplift you asked for.',
        basis: upliftText ? `${amountText} against a ${upliftText} uplift.` : `${amountText}.`,
        tone: 'go',
      };
    }
    if (residual !== null) {
      const residualText = formatUsd(residual);
      return {
        line: `You can commit to ${amountText} today; ${residualText} of the uplift is still to find.`,
        basis: upliftText
          ? `${amountText} against a ${upliftText} uplift; ${residualText} remains.`
          : `${amountText} committable; ${residualText} remains.`,
        tone: 'watch',
      };
    }
    return {
      line: `You can commit to ${amountText} today; how far that goes against the uplift is still open.`,
      basis: upliftText ? `${amountText} against a ${upliftText} uplift.` : `${amountText}.`,
      tone: 'watch',
    };
  }

  if (
    primary.posture === 'management_package' &&
    primary.amountUsd !== null
  ) {
    const amountText = formatUsd(primary.amountUsd);
    const residual =
      primary.residual?.basis === 'measured'
        ? primary.residual.amountUsd
        : null;
    const dependableMinimumText =
      primary.postureMix.receiptedAmountUsd !== null
        ? formatUsd(primary.postureMix.receiptedAmountUsd)
        : null;
    const selectedEstimateText =
      primary.postureMix.modeledAmountUsd !== null
        ? formatUsd(primary.postureMix.modeledAmountUsd)
        : null;
    const basisParts: string[] = [];
    if (dependableMinimumText) {
      basisParts.push(`Dependable minimum: ${dependableMinimumText}.`);
    }
    if (selectedEstimateText) {
      basisParts.push(`Selected estimate: ${selectedEstimateText}.`);
    }

    if (residual !== null && residual > 0) {
      const residualText = formatUsd(residual);
      basisParts.push(
        upliftText
          ? `Management package: ${amountText} against a ${upliftText} uplift; ${residualText} remains.`
          : `Management package: ${amountText}; ${residualText} remains.`,
      );
      return {
        line:
          `The selected management package provides ${amountText}; ` +
          `${residualText} of the uplift is still to close.`,
        basis: basisParts.join(' '),
        tone: 'watch',
      };
    }

    if (residual !== null) {
      basisParts.push(
        upliftText
          ? `Management package: ${amountText} against a ${upliftText} uplift; the target is covered on this management plan.`
          : `Management package: ${amountText}; the target is covered on this management plan.`,
      );
      return {
        line:
          'The selected management package covers the uplift, including the selected estimate that still requires validation.',
        basis: basisParts.join(' '),
        tone: 'watch',
      };
    }

    basisParts.push(
      upliftText
        ? `Management package: ${amountText} against a ${upliftText} uplift; the remaining gap is not yet measured.`
        : `Management package: ${amountText}; the remaining gap is not yet measured.`,
    );
    return {
      line:
        `The selected management package provides ${amountText}; ` +
        'how far it goes against the uplift is still open.',
      basis: basisParts.join(' '),
      tone: 'watch',
    };
  }

  const planningText = planningLensAmount !== null ? formatUsd(planningLensAmount) : null;
  const basisParts: string[] = [];
  if (upliftText) basisParts.push(`${upliftText} uplift to close.`);
  if (planningText) {
    basisParts.push(`${planningText} is on the table for planning, but it is not ready to commit to.`);
  }
  if (primary.hold) basisParts.push(primary.hold.reasonPlain);
  return {
    line: 'There is no amount you can commit to yet.',
    basis: basisParts.join(' ').trim() || 'No combined amount has been published on this answer.',
    // A sized planning view is a route, even though it is not committable — that
    // is 'watch', not 'stop'. 'stop' is reserved for an answer with nothing
    // sized at all, so the tone stays informative instead of uniformly alarmed.
    tone: planningLensAmount !== null ? 'watch' : 'stop',
  };
}

// ---------------------------------------------------------------------------
// Status ontology
// ---------------------------------------------------------------------------

const GOVERNANCE_AXIS_LABELS: Record<ExecutiveGovernanceFacetAxisId, string> = {
  source: 'Approved source behind the number',
  formula: 'Calculation connected',
  owner: 'Named owner',
  overlap: 'Double-count check',
  realization: 'Realization evidence',
};

function buildGovernanceAxes(
  counts: ExecutiveAnswerGovernanceFacetCountsInput
): ExecutiveGovernanceAxisCount[] {
  return EXECUTIVE_GOVERNANCE_FACET_AXIS_IDS.filter((axisId) => counts[axisId] !== undefined).map(
    (axisId) => {
      const row = counts[axisId] as { live: number; modeled: number; notYetArmed: number };
      return {
        axisId,
        label: GOVERNANCE_AXIS_LABELS[axisId],
        live: row.live,
        modeled: row.modeled,
        notYetArmed: row.notYetArmed,
        total: row.live + row.modeled + row.notYetArmed,
      };
    }
  );
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

/**
 * Assemble THE canonical answer state. Called exactly once per answer, on the
 * server. Every surface binds to the result.
 */
export function buildExecutiveAnswerState(
  input: ExecutiveAnswerStateInput
): ExecutiveAnswerState {
  const packageAuthority = input.packageSpine?.packageAuthority ?? null;
  const packageCoverage = input.packageSpine?.coverage ?? null;
  const eligibility = evaluateCandidateEligibility(input.candidateGovernance);

  // --- candidate lens (the judged $124.4M) ----------------------------------
  const candidatePublished = input.candidatePackage.value !== null;
  const candidatePosture: ExecutiveReferenceLens['posture'] = !candidatePublished
    ? 'absent'
    : input.candidatePackage.lineage.state === 'conflicted'
      ? 'candidate_unresolved_lineage'
      : eligibility.armed
        ? 'candidate_decidable'
        : 'candidate_not_armed';
  const candidateLens: ExecutiveReferenceLens = {
    lensId: 'selected_target_gap_candidate',
    role: 'candidate',
    label: 'Candidate package for the gap',
    amountUsd: input.candidatePackage.value,
    posture: candidatePosture,
    usableFor: candidatePublished ? 'decision_candidate_review' : 'nothing_yet',
    usableForPlain:
      candidatePosture === 'candidate_unresolved_lineage'
        ? 'Reviewing what could be decided once its own records agree with each other. Not a ' +
          'figure to approve while they disagree.'
        : candidatePosture === 'candidate_decidable'
          ? 'The figure leadership can be asked to decide on — every check behind it is ' +
            'complete and current.'
          : 'Reviewing what could be decided once its outstanding checks are complete. Not a ' +
            'figure to approve yet.',
    memberIds: input.candidatePackage.memberIds,
    provenance: input.candidatePackage.provenance,
    residual: null,
    residualWithholding: NO_MEASURED_BASIS_WITHHOLDING,
    targetFillPct: null,
    lineage: input.candidatePackage.lineage,
    hold: candidatePublished
      ? null
      : slotHold(
          input.candidatePackage,
          'A candidate package for the gap',
          'Publish the candidate package and the gap remaining after it together — one ' +
            'without the other cannot be read.'
        ),
  };

  // --- planning lens (the judged $305.8M) -----------------------------------
  const planningPublished = input.planningLens.value !== null;
  const planningLens: ExecutiveReferenceLens = {
    lensId: 'declared_overlap_planning_lens',
    role: 'planning_lens',
    label: 'Planning estimate, adjusted for declared shared dollars',
    amountUsd: input.planningLens.value,
    posture: planningPublished ? 'planning_only' : 'absent',
    usableFor: planningPublished ? 'planning_and_target_setting' : 'nothing_yet',
    // Sol's semantic table: this is "net after the maximum deduction across
    // currently DECLARED overlap pairs" — not risk-adjusted, not a worst case,
    // and it carries modeled and gated inputs. Say all three out loud.
    usableForPlain:
      'Planning and target-setting inside the company. It is the candidate set less the ' +
      'largest deduction the pairs already declared to share a cost base could cause — ' +
      'execution risk and timing are not deducted, and pairs nobody has declared are not ' +
      'deducted at all. Never a commitment to anyone outside the company.',
    memberIds: input.planningLens.memberIds,
    provenance: input.planningLens.provenance,
    // The planning lens's own published remaining gap. It is a projection of a
    // figure the assembler already published against the SAME basis — never the
    // solver's package residual, which belongs to the package, and never a
    // subtraction performed here.
    residual:
      input.planningLens.publishedResidualUsd == null
        ? null
        : { basis: 'measured', amountUsd: input.planningLens.publishedResidualUsd },
    residualWithholding:
      input.planningLens.publishedResidualUsd == null ? NO_MEASURED_BASIS_WITHHOLDING : null,
    // Target FILL stays null: a planning figure carrying modeled and gated
    // inputs may state what is left against its own basis, but it may not claim
    // a share of the target as covered.
    targetFillPct: null,
    lineage: { state: 'consistent' },
    hold: planningPublished
      ? null
      : slotHold(
          input.planningLens,
          'A planning estimate',
          'Publish the planning estimate for the moves sized on this answer.'
        ),
  };

  // --- standalone inventory (the judged $182.3M) ----------------------------
  const standalonePublished = input.standaloneInventory.value !== null;
  const standaloneLens: ExecutiveReferenceLens = {
    lensId: 'standalone_opportunity_inventory',
    role: 'standalone_inventory',
    label: 'Each move on its own',
    amountUsd: input.standaloneInventory.value,
    posture: standalonePublished ? 'not_additive' : 'absent',
    usableFor: standalonePublished ? 'per_move_sizing' : 'nothing_yet',
    usableForPlain:
      'Sizing each move on its own. These figures must not be added together — the same ' +
      'dollar can appear in more than one of them.',
    memberIds: input.standaloneInventory.memberIds,
    provenance: input.standaloneInventory.provenance,
    // NULL. Sol's #4095 section 1: the semantic table says a non-additive lens
    // has residual AND targetFill null; the reason lives beside it so nothing
    // is suppressed.
    residual: null,
    residualWithholding: NOT_ADDITIVE_RESIDUAL_WITHHOLDING,
    targetFillPct: null,
    lineage: { state: 'consistent' },
    hold: standalonePublished
      ? null
      : slotHold(
          input.standaloneInventory,
          'A standalone total',
          'Publish the standalone total for the moves sized on this answer.'
        ),
  };

  // --- plan of record --------------------------------------------------------
  const planOfRecordPublished = input.planOfRecord.value !== null;
  const planOfRecordLens: ExecutiveReferenceLens = {
    lensId: 'approved_plan_of_record',
    role: 'plan_of_record',
    label: 'The plan already approved',
    amountUsd: input.planOfRecord.value,
    posture: planOfRecordPublished ? 'plan_of_record' : 'absent',
    usableFor: planOfRecordPublished ? 'reconciliation_against_plan' : 'nothing_yet',
    usableForPlain:
      'The company’s own approved plan. Read the package against it — it is the bar this ' +
      'answer has to clear, not a figure this answer produced.',
    memberIds: input.planOfRecord.memberIds,
    provenance: input.planOfRecord.provenance,
    residual: null,
    residualWithholding: NO_MEASURED_BASIS_WITHHOLDING,
    targetFillPct: null,
    lineage: { state: 'consistent' },
    hold: planOfRecordPublished
      ? null
      : slotHold(
          input.planOfRecord,
          'The approved plan of record',
          'Connect the approved plan of record for this measure and period.'
        ),
  };

  const referenceLenses: ExecutiveReferenceLens[] = [
    candidateLens,
    planningLens,
    standaloneLens,
    planOfRecordLens,
  ];

  // --- primary selection -----------------------------------------------------
  // Every lens is explicitly considered and explicitly rejected, by ROLE first.
  // No lens can become the primary by being large.
  const rejected: ExecutivePrimaryRejection[] = [
    {
      candidateId: planningLens.lensId,
      reasonCode: 'role_never_primary',
      reasonPlain:
        'A planning estimate is never the figure you commit to: it deducts the largest ' +
        'possible double-count rather than a measured one, and it carries values that are ' +
        'modeled or still waiting on an authorization decision.',
    },
    {
      candidateId: standaloneLens.lensId,
      reasonCode: 'not_additive',
      reasonPlain:
        'These moves are sized on their own and have not been shown to release separate ' +
        'dollars, so they cannot be added into one figure at all.',
    },
    {
      candidateId: planOfRecordLens.lensId,
      reasonCode: 'role_never_primary',
      reasonPlain:
        'The approved plan is the bar this answer has to clear. It is not something this ' +
        'answer can offer back as its own result.',
    },
  ];
  if (candidatePublished) {
    rejected.push({
      candidateId: candidateLens.lensId,
      reasonCode:
        input.candidatePackage.lineage.state === 'conflicted'
          ? 'lineage_conflict'
          : eligibility.armed
            ? 'not_receipt_backed'
            : 'governance_not_armed',
      reasonPlain:
        input.candidatePackage.lineage.state === 'conflicted'
          ? input.candidatePackage.lineage.conflictPlain
          : eligibility.armed
            ? 'Its members have not been shown to release separate dollars from each other ' +
              'under the one approved double-count standard, so it cannot be committed to as ' +
              'a single figure.'
            : `Its outstanding checks are not complete: ${eligibility.conditionsUnmet.join('; ')}.`,
    });
  } else {
    rejected.push({
      candidateId: candidateLens.lensId,
      reasonCode: input.candidatePackage.threadingPending ? 'threading_pending' : 'not_published',
      reasonPlain: candidateLens.hold?.reasonPlain ?? 'No candidate package is published.',
    });
  }

  const primaryDecisionPackage = buildPrimaryDecisionPackage({
    floor: input.committedFloor,
    candidateRejections: rejected,
    packageSpine: input.packageSpine,
    packageAuthority,
  });

  // --- workforce -------------------------------------------------------------
  const workforce: ExecutiveWorkforceState = {
    inclusion: input.workforce.inclusion,
    amountUsd: input.workforce.inclusion === 'absent' ? null : input.workforce.amountUsd,
    countedInLensIds:
      input.workforce.inclusion === 'excluded' ? [] : input.workforce.countedInLensIds,
    statementPlain:
      input.workforce.inclusion === 'in_primary'
        ? 'Workforce moves are inside the amount you could commit to.'
        : input.workforce.inclusion === 'in_planning_only'
          ? 'Workforce moves are inside the planning estimate and outside every amount you ' +
            'could commit to. Authorizing or declining them is what settles which.'
          : input.workforce.inclusion === 'excluded'
            ? 'Workforce moves are outside every figure on this answer, including the ' +
              'planning estimate.'
            : 'No workforce move is sized on this answer.',
    hold:
      input.workforce.inclusion === 'absent' && input.workforce.threadingPending
        ? threadingHold('The workforce position on this answer')
        : null,
  };

  // --- scenario --------------------------------------------------------------
  const scenarioRelevant =
    input.scenario.scenarioMetricId !== null &&
    input.scenario.answeredMetricId !== null &&
    input.scenario.scenarioMetricId === input.scenario.answeredMetricId;
  const scenario: ExecutiveScenarioSelection = {
    selectedCaseId: input.scenario.criteriaApplied.length > 0 ? input.scenario.selectedCaseId : null,
    criteriaApplied: input.scenario.criteriaApplied,
    scenarioMetricId: input.scenario.scenarioMetricId,
    relevantToAnsweredMetric: scenarioRelevant,
    promotedIntoAnswer:
      scenarioRelevant &&
      input.scenario.criteriaApplied.length > 0 &&
      input.scenario.selectedCaseId !== null,
    hold:
      input.scenario.selectedCaseId !== null && input.scenario.criteriaApplied.length === 0
        ? policyHold(
            'This sweep states no basis on which to prefer one of its cases, so none of them ' +
              'is presented as the plan of record.',
            'State an execution-risk class per case and the preferred case follows.'
          )
        : !scenarioRelevant && input.scenario.scenarioMetricId !== null
          ? policyHold(
              'This sweep is about a different measure from the one you asked about, so it is ' +
                'not folded into this answer.',
              'Ask about that measure directly to see the sweep in full.'
            )
          : null,
  };

  return {
    schemaVersion: EXECUTIVE_ANSWER_STATE_SCHEMA_VERSION,
    overlapReceiptStandard: EXECUTIVE_ANSWER_OVERLAP_RECEIPT_STANDARD,
    metric: buildMetric(input.metric),
    objective: {
      baseline: {
        amountUsd: input.objective.baseline.value,
        hold:
          input.objective.baseline.value === null
            ? objectiveHold('where the measure sits today', input.objective.baseline)
            : null,
      },
      target: {
        amountUsd: input.objective.target.value,
        hold:
          input.objective.target.value === null
            ? objectiveHold('the approved target', input.objective.target)
            : null,
      },
      requiredUplift: {
        amountUsd: input.objective.requiredUplift.value,
        hold:
          input.objective.requiredUplift.value === null
            ? objectiveHold('the uplift you asked for', input.objective.requiredUplift)
            : null,
      },
    },
    packageSpine: input.packageSpine,
    packageAuthority,
    coverage: packageCoverage,
    primaryDecisionPackage,
    referenceLenses,
    status: {
      evidencePosture: input.evidencePostureCounts,
      governanceFacetAxes: buildGovernanceAxes(input.governanceFacetCounts),
      headerCountingAxisId: EXECUTIVE_HEADER_COUNTING_AXIS_ID,
      headerCountingAxisLabel: 'How each move is evidenced',
    },
    workforce,
    scenario,
    verdict: buildVerdict({
      primary: primaryDecisionPackage,
      requiredUplift: input.objective.requiredUplift.value,
      planningLensAmount: planningLens.amountUsd,
      formatUsd: input.formatUsd,
    }),
  };
}
